import { useGPFormContext } from '@/contexts/GPFormContext';
import { Box, FormControl, IconButton, InputLabel, OutlinedInput, Stack } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Gpsearch } from 'givepulse-iconsax-react';
import React from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles()(theme => ({
  searchForm: {
    width: '100%',
  },

  nameField: {
    flex: 1,
  },
  iconButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.background.default,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  iconButtonWrapped: {
    marginLeft: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

type GPSearchFieldProps = {
  id: string;
  name: string;
  hint?: string;
  showSearchButton?: boolean;
};

const GPSearchField = ({ id, name, hint, showSearchButton = true }: GPSearchFieldProps) => {
  const { handleSubmit, onSubmit, valueState, modified, defaultValues, setModified, watch } =
    useGPFormContext();
  const { classes } = useStyles();
  const { t } = useTranslation();

  React.useEffect(() => {
    if (!defaultValues[name] && !valueState[name]) {
      modified.delete(name) && setModified(new Set(modified));
      return;
    }
    if (defaultValues[name] != valueState[name]) {
      !modified.has(name) && setModified(new Set(modified.add(name)));
    } else {
      modified.delete(name) && setModified(new Set(modified));
    }
  }, [valueState, defaultValues, setModified, name, modified]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(onSubmit)();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  };

  const handleApply = () => {
    handleSubmit(onSubmit)();
  };

  const { field } = useController({ name, rules: { maxLength: 100 } });

  return (
    <FormControl variant="outlined" className={classes.searchForm}>
      <Stack
        sx={{
          gap: 1,
          mb: 3
        }}>
        <InputLabel htmlFor={id}>{hint}</InputLabel>
        <Stack sx={{ flexDirection: 'row', alignItems: 'stretch', maxWidth: '500px' }}>
          <OutlinedInput
            id={id}
            className={classes.nameField}
            label={hint}
            onKeyPress={handleKeyDown}
            {...field}
            value={watch(name) === undefined ? '' : watch(name)}
          />

          {showSearchButton && (
            <Box className={classes.iconButtonWrapped}>
              <IconButton
                size="large"
                color="primary"
                aria-label={t('Search', { ns: 'dashboard' })}
                component="span"
                className={classes.iconButton}
                onClick={handleApply}
              >
                <Gpsearch />
              </IconButton>
            </Box>
          )}
        </Stack>
      </Stack>
    </FormControl>
  );
};

export default GPSearchField;
